import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import HelpOutline from "@material-ui/icons/HelpOutline";
import CompareArrows from "@material-ui/icons/CompareArrows";
import FindInPage from "@material-ui/icons/FindInPage";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";

import { answerInputDecisionV3 } from "../../../../services/inputsV3Service";

// Resolves a multilingual value ({en, pt, es} dict or string) to the
// current language, with fallback chain en → pt → any.
function renderI18n(value, lang) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    if (value.startsWith("{") || value.startsWith("[")) {
      try {
        return renderI18n(JSON.parse(value), lang);
      } catch (_) {
        return value;
      }
    }
    return value;
  }
  if (typeof value !== "object") return String(value);
  const short = (lang || "pt").slice(0, 2);
  return value[short] || value.pt || value.en || value.es || "";
}

// Minimal inline markdown: **bold** → <strong>, `code` → <code>.
// Deterministic, no external dependencies, no XSS risk.
function renderInlineMarkdown(text, classes) {
  if (typeof text !== "string" || text.length === 0) return text;
  const codeParts = text.split(/`([^`]+)`/g);
  return codeParts.flatMap((chunk, codeIdx) => {
    if (codeIdx % 2 === 1) {
      return [
        <code
          key={`c-${codeIdx}`}
          className={classes ? classes.inlineCode : undefined}
        >
          {chunk}
        </code>,
      ];
    }
    const boldParts = chunk.split(/\*\*([^*]+)\*\*/g);
    return boldParts.map((piece, boldIdx) => {
      if (boldIdx % 2 === 1) {
        return <strong key={`c-${codeIdx}-b-${boldIdx}`}>{piece}</strong>;
      }
      return piece;
    });
  });
}

function fmtPercent(value, lang, naLabel) {
  if (typeof value !== "number") return naLabel;
  const raw = (value * 100).toFixed(1);
  const sep = (lang || "pt").slice(0, 2) === "en" ? "." : ",";
  return raw.replace(".", sep) + "%";
}

function scopeIcon(scope) {
  if (scope === "cross_link") return CompareArrows;
  if (scope === "input_row_edit") return FindInPage;
  return HelpOutline;
}

function DecisionCard({ decision, classes, onAnswered, onSubmittingChange }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "pt";

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // ``submittingRef`` mirrora ``submitting`` para uso no cleanup do
  // ``useEffect`` de unmount — closures do useState ficam stale.
  const submittingRef = useRef(false);
  const onSubmittingChangeRef = useRef(onSubmittingChange);
  useEffect(() => {
    onSubmittingChangeRef.current = onSubmittingChange;
  }, [onSubmittingChange]);

  const options = Array.isArray(decision.options) ? decision.options : [];
  const ScopeIcon = scopeIcon(decision.scope);

  const notifyParent = (next) => {
    const cb = onSubmittingChangeRef.current;
    if (typeof cb === "function") {
      try {
        cb(next);
      } catch (_) {
        // ignore — callback failure must not block UI
      }
    }
  };

  // Onda 3.4 (CCU Testes, mai/2026 — refinamento): o pai (drawer) so
  // pode ser liberado quando o card de fato desmonta apos uma resposta
  // bem-sucedida. Antes liberavamos ja no retorno da API, mas o
  // refresh dos detalhes do drawer (que e' quem desmonta o card)
  // demora alguns frames e o usuario reportou conseguir fechar o
  // drawer nessa janela. Cleanup do useEffect cobre exatamente isso:
  // ao desmontar, garante release.
  useEffect(() => {
    return () => {
      if (submittingRef.current) {
        submittingRef.current = false;
        const cb = onSubmittingChangeRef.current;
        if (typeof cb === "function") {
          try {
            cb(false);
          } catch (_) {
            // ignore
          }
        }
      }
    };
  }, []);

  const handleChoose = async (idx) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    notifyParent(true);
    setError(null);
    try {
      await answerInputDecisionV3(decision.id, {
        chosenIndex: idx,
        rememberForFuture: false,
      });
      try {
        window.dispatchEvent(new CustomEvent("inputDecisionAnsweredV3"));
      } catch (_) {
        // dispatchEvent failure is non-fatal; ignore silently.
      }
      // ``onAnswered`` dispara o refresh dos detalhes do drawer. Em
      // condicao normal, esse refresh retorna a decisao em
      // ``state=answered`` e este ``DecisionCard`` e desmontado pelo
      // pai — o cleanup do ``useEffect`` libera o ``decisionSubmitting``
      // do drawer naquele momento (nao aqui). Manter ``submitting=true``
      // local ate o desmonte evita o flicker "pergunta volta ao normal
      // antes de sumir".
      if (onAnswered) onAnswered(decision);
    } catch (err) {
      const code = err?.response?.data?.error_code;
      if (code === "decision_already_answered") {
        // Server diz "ja respondida": refresh do pai vai desmontar o
        // card normalmente, cleanup libera o drawer.
        if (onAnswered) onAnswered(decision);
        return;
      }
      // Erro real — restaura UI para nova tentativa. Libera o pai
      // aqui mesmo porque o card NAO vai desmontar.
      setError(
        code === "invalid_chosen_index"
          ? t("modeling_inputs_v3.decisions.error_invalid_option")
          : t("modeling_inputs_v3.decisions.error_save_failed"),
      );
      submittingRef.current = false;
      setSubmitting(false);
      notifyParent(false);
    }
  };

  const crossLinkDisplay =
    decision.scope === "cross_link"
      ? decision.cross_link_display_name || decision.meta_name || ""
      : "";
  const titleSuffix = crossLinkDisplay
    ? ` · ${crossLinkDisplay}`
    : decision.meta_name
      ? ` · ${decision.meta_name}`
      : "";

  const stats = decision.measure || null;
  const questionText =
    renderI18n(decision.question_md, lang) ||
    t("modeling_inputs_v3.decisions.no_statement");

  return (
    <div
      className={classes.decisionsCard}
      role="region"
      aria-label={`${t("modeling_inputs_v3.decisions.pending_header")}${titleSuffix}`}
    >
      <div className={classes.decisionsCardHeader}>
        <ScopeIcon />
        <span className={classes.decisionsCardTitle}>
          {t("modeling_inputs_v3.decisions.pending_header")}
          {titleSuffix}
        </span>
        {submitting ? (
          <span
            className={classes.decisionsCardSubtitle}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <CircularProgress
              size={12}
              thickness={5}
              style={{ color: "#ffffff" }}
            />
            {t("modeling_inputs_v3.decisions.saving")}
          </span>
        ) : (
          <span className={classes.decisionsCardSubtitle}>
            {t("modeling_inputs_v3.decisions.awaiting_answer")}
          </span>
        )}
      </div>
      <div className={classes.pendingDecisionBody}>
        <div className={classes.pendingDecisionQuestion}>
          {renderInlineMarkdown(questionText, classes)}
        </div>
        {stats && (
          <div className={classes.pendingDecisionStats}>
            {typeof stats.coverage === "number" && (
              <span>
                <span className={classes.pendingDecisionStatKey}>
                  {t("modeling_inputs_v3.decisions.stats_coverage")}
                </span>
                <span
                  className={`${classes.pendingDecisionStatValue} ${classes.pendingDecisionStatValueBad}`}
                >
                  {fmtPercent(
                    stats.coverage,
                    lang,
                    t("modeling_inputs_v3.decisions_card.value_na"),
                  )}
                </span>
              </span>
            )}
            {typeof stats.limit === "number" && (
              <span>
                <span className={classes.pendingDecisionStatKey}>
                  {t("modeling_inputs_v3.decisions.stats_limit")}
                </span>
                <span className={classes.pendingDecisionStatValue}>
                  {fmtPercent(
                    stats.limit,
                    lang,
                    t("modeling_inputs_v3.decisions_card.value_na"),
                  )}
                </span>
              </span>
            )}
            {/* Onda 3.4 (CCU Testes, mai/2026): a linha "Critério:
                crítico" foi removida — a D-22 so existe quando o
                limite critico foi rompido, logo o rotulo era
                redundante (carregava 100% da informacao ja transmitida
                pelo enunciado + pelas opcoes em vermelho). */}
          </div>
        )}
      </div>
      <div className={classes.pendingDecisionChoices}>
        {options.length === 0 ? (
          <span style={{ fontSize: 12, color: "#777" }}>
            {t("modeling_inputs_v3.decisions.empty_options")}
          </span>
        ) : (
          options.map((opt, idx) => {
            const value = opt?.value || {};
            const isAccept = value.decision === "accept";
            const isReject = value.decision === "reject_input";
            // Label das OPCOES vem do i18n do front (segue o idioma da
            // UI dinamicamente). O backend tambem manda ``opt.label``
            // (humanizado no idioma da implementacao), mas usamos
            // apenas como fallback final para payloads legados ou
            // ``decision`` desconhecida. Pergunta (``question_md``)
            // segue no idioma da implementacao — sao concepts
            // diferentes (pergunta tem humanizacao complexa e e' a
            // mesma para todos os usuarios; resposta e' clique simples
            // que se traduz fluida com o i18n).
            let label;
            if (isAccept) {
              label = t("modeling_inputs_v3.decisions.label_accept");
            } else if (isReject) {
              const inputName =
                value.input_display_name || value.input_meta_name || "?";
              label = t("modeling_inputs_v3.decisions.label_reject", {
                input: inputName,
              });
            } else {
              label =
                renderI18n(opt?.label, lang) ||
                t("modeling_inputs_v3.decisions_card.option_fallback", {
                  n: idx + 1,
                });
            }
            const cardClass = isAccept
              ? classes.pendingDecisionChoiceAccept
              : classes.pendingDecisionChoiceReject;
            const iconClass = isAccept
              ? classes.pendingDecisionChoiceIconAccept
              : classes.pendingDecisionChoiceIconReject;
            const Icon = isAccept ? Check : Close;
            const effectKey =
              value.effect === "proceeds"
                ? "effect_proceeds"
                : value.effect === "blocks"
                  ? "effect_blocks"
                  : null;
            const caption = effectKey
              ? t(`modeling_inputs_v3.decisions.${effectKey}`)
              : null;
            return (
              <button
                key={idx}
                type="button"
                className={`${classes.pendingDecisionChoice} ${cardClass}`}
                disabled={submitting}
                onClick={() => handleChoose(idx)}
                title={renderI18n(opt?.tooltip, lang) || undefined}
              >
                <span
                  className={`${classes.pendingDecisionChoiceIcon} ${iconClass}`}
                >
                  <Icon />
                </span>
                <span className={classes.pendingDecisionChoiceLabel}>
                  {label}
                </span>
                {caption && (
                  <span className={classes.pendingDecisionChoiceEffect}>
                    {caption}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
      {error && <div className={classes.decisionError}>{error}</div>}
    </div>
  );
}

DecisionCard.propTypes = {
  decision: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onAnswered: PropTypes.func,
  onSubmittingChange: PropTypes.func,
};

// Wrapper that renders N independent decision cards stacked vertically.
const DecisionsCard = ({
  decisions,
  classes,
  onAnswered,
  onSubmittingChange,
}) => {
  if (!decisions || decisions.length === 0) return null;
  return (
    <div className={classes.decisionsStack}>
      {decisions.map((d) => (
        <DecisionCard
          key={d.id}
          decision={d}
          classes={classes}
          onAnswered={onAnswered}
          onSubmittingChange={onSubmittingChange}
        />
      ))}
    </div>
  );
};

DecisionsCard.propTypes = {
  decisions: PropTypes.array,
  classes: PropTypes.object.isRequired,
  onAnswered: PropTypes.func,
  onSubmittingChange: PropTypes.func,
};

export default DecisionsCard;
